import { useCallback, useEffect, useState } from 'react'

import { Plus} from "@gravity-ui/icons"
import {Car} from '@gravity-ui/icons'
import { Button, type SortDescriptor } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageHeader } from '@/components/ui/PageHeader'
import { useVehiclesStore } from '@/store/vehicles.store'
import { SortDirection } from '@/utils/sorting'

import { VehicleDialog } from './components/VehicleDialog'
import { VehiclesTable } from './components/VehiclesTable'
export default function VehiclesList() {
  const { t } = useTranslation()
  const { vehicles, deleteVehicle, isLoading: isStoreLoading, setPagination, pagination } = useVehiclesStore()

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'plate',
    direction: 'ascending',
  })

  useEffect(() => {
    const apiSortKey = {
      plate: 'number',
      brand: 'model',
      year: 'make',
    }[sortDescriptor.column as string] || (sortDescriptor.column as string)

    setPagination({
      sort: {
        key: apiSortKey,
        direction: sortDescriptor.direction === 'ascending' ? SortDirection.ASC : SortDirection.DESC,
      },
    })
  }, [setPagination, sortDescriptor])

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | undefined>()
  const [deletingId, setDeletingId] = useState<string | undefined>()

  const handleAdd = useCallback(() => {
    setEditingId(undefined)
    setIsModalOpen(true)
  }, [])

  const handleEdit = (id: string) => {
    setEditingId(id)
    setIsModalOpen(true)
  }

  const handleDeleteTrigger = (id: string) => {
    setDeletingId(id)
    setIsDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (deletingId) {
      await deleteVehicle(deletingId)
      setIsDeleteOpen(false)
      setDeletingId(undefined)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingId(undefined)
  }

  return (
    <div className="p-5 space-y-5">
      <PageHeader
        title={t('vehicles.title')}
        subtitle={`${pagination.total} ${t('vehicles.subtitle')}`}
        actions={
          <Button
            onClick={handleAdd}
          >
            <Plus  />
            {t('vehicles.add_vehicle')}
          </Button>
        }
      />

      {vehicles.length === 0 ? (
        <EmptyState
          icon={<Car className={'size-8'} />}
          title={t('vehicles.empty_title')}
          description={t('vehicles.empty_subtitle')}
        >
          <Button size="lg" onClick={handleAdd}>
            <Plus />
            {t('vehicles.empty_action')}
          </Button>
        </EmptyState>
      ) : (
        <VehiclesTable
          vehicles={vehicles}
          onEdit={handleEdit}
          onDelete={handleDeleteTrigger}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        />
      )}

      <VehicleDialog
        isOpen={isModalOpen}
        onOpenChange={open => !open && closeModal()}
        vehicleId={editingId}
      />

      <ConfirmDeleteDialog
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete')}
        description={t('vehicles.delete_confirm')}
        isLoading={isStoreLoading}
      />
    </div>
  )
}
